import React, { FC, useEffect, useState } from 'react';
import styles from './LandscapeIndicatorVerificationApproachPage.module.css';
import { LandscapeIndicatorVerificationApproachTemplate } from '../../templates';
import {
  getLanguageKey,
  LocalesLanguagesConstants,
} from '../../../locales/locales';
import { RichTextBlockResponseDto } from '../../../store/landscapeAPI/block/dto/response/rich-text-block-response.dto';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../store/store';
import Spinner from '../../atoms/Spinner/Spinner';
import {
  fetchBlocks,
  saveRichTextBlock,
  updateRichTextBlock,
} from '../../../store/landscapeAPI/block/reducers/block.slice';
import { EntityBlockMappingResponseDto } from '../../../store/landscapeAPI/block/dto/response/entity-block-mapping-response.dto';
import { RichTextBlockUpdateRequestDto } from '../../../store/landscapeAPI/block/dto/request/rich-text-block-update-request.dto';
import { BlockType } from '../../../store/landscapeAPI/block/dto/block-type';
import { getUUID } from '../../../utils/UUID';
import { RichTextBlockSaveRequestDto } from '../../../store/landscapeAPI/block/dto/request/rich-text-block-save-request.dto';
import {
  translateObject,
  TranslateParams,
} from '../../../store/translateAPI/translate/reducers/translate.slice';
import { showSuccess } from '../../../utils/Notifications';
interface LandscapeIndicatorVerificationApproachPageProps {}

const LandscapeIndicatorVerificationApproachPage: FC<
  LandscapeIndicatorVerificationApproachPageProps
> = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { id, landscapeIndicatorId } = useParams();
  const [selectedLanguage, setLanguage] = useState<LocalesLanguagesConstants>(
    LocalesLanguagesConstants.en
  );
  const [fromLanguage, setFromLanguage] = useState<LocalesLanguagesConstants>(
    LocalesLanguagesConstants.en
  );
  const [toLanguage, setToLanguage] = useState<LocalesLanguagesConstants>(
    LocalesLanguagesConstants.en
  );

  const generateAllTranslations = async () => {
    return await Promise.allSettled(
      Object.values(LocalesLanguagesConstants).map(translate)
    );
  };

  const [content, setContent] = useState<string>('');
  const [published, setPublished] = useState<boolean>(true);
  const [textBlock, setTextBlock] = useState<EntityBlockMappingResponseDto>({
    entityType: 'compactIndicator',
    entityId: landscapeIndicatorId!,
    sortOrder: 0,
    block: {
      content: '',
      blockType: BlockType.RichTextBlock,
      compactId: id!,
      id: getUUID(),
      versionId: 0,
      createdByUserId: '',
      createdOnDateTimeUtc: '',
      language: '',
      published: true,
    },
    id: '',
    versionId: 0,
    createdByUserId: '',
    createdOnDateTimeUtc: '',
    language: '',
    published: true,
  });

  const { richTextBlockStatus: status } = useSelector(
    (state: RootState) => state.blockSlice
  );

  const { status: translateStatus } = useSelector(
    (state: RootState) => state.translateSlice
  );

  useEffect(() => {
    dispatch(
      fetchBlocks({
        id: landscapeIndicatorId!,
        type: 'compactIndicator',
        language: selectedLanguage,
      })
    )
      .unwrap()
      .then((resp) => {
        if (resp.length > 0) {
          setTextBlock(resp[0]);
          setContent(
            (resp[0].block as RichTextBlockResponseDto)?.content ?? ''
          );
          setPublished(
            (resp[0].block as RichTextBlockResponseDto)?.published ?? ''
          );
        }
      });
  }, [dispatch, landscapeIndicatorId, selectedLanguage]);

  const translate = async (toLanguage: LocalesLanguagesConstants) => {
    const params: TranslateParams<RichTextBlockResponseDto> = {
      obj: textBlock.block as RichTextBlockResponseDto,
      from: getLanguageKey(fromLanguage)!,
      to: [getLanguageKey(toLanguage)!],
    };
    const response = await dispatch(translateObject(params)).unwrap();

    if (textBlock.id !== '') {
      const updateReq: RichTextBlockUpdateRequestDto = {
        content: (response as RichTextBlockResponseDto).content,
        blockType: (textBlock.block as RichTextBlockResponseDto).blockType,
        compactId: (textBlock.block as RichTextBlockResponseDto).compactId,
        id: (textBlock.block as RichTextBlockResponseDto).id,
        published: (textBlock.block as RichTextBlockResponseDto).published,
      };

      setLanguage(toLanguage);

      dispatch(updateRichTextBlock({ data: updateReq, language: toLanguage }));
    }
  };

  const generateTranslation = async () => {
    return await translate(toLanguage);
  };

  const callbackFn = async () => {
    if (textBlock.id !== '') {
      const textBlockToUpdate: RichTextBlockUpdateRequestDto = {
        content: content,
        blockType: (textBlock.block as RichTextBlockResponseDto).blockType,
        compactId: (textBlock.block as RichTextBlockResponseDto).compactId,
        id: (textBlock.block as RichTextBlockResponseDto).id,
        published: published,
      };
      await dispatch(
        updateRichTextBlock({
          data: textBlockToUpdate,
          language: selectedLanguage,
        })
      );
    } else {
      const textBlockToSave: RichTextBlockSaveRequestDto = {
        content: content,
        blockType: (textBlock.block as RichTextBlockResponseDto).blockType,
        entityType: textBlock.entityType ?? 'compactIndicator',
        entityId: textBlock.entityId,
        compactId: (textBlock.block as RichTextBlockResponseDto).compactId,
        id: getUUID(),
        published: published,
      };
      await dispatch(saveRichTextBlock(textBlockToSave));
    }

    showSuccess('Verification approach saved successfully.');
    navigate(`/landscapes/edit/${id}/indicators`);
  };

  if (status === 'loading' || translateStatus === 'loading') {
    return <Spinner />;
  }

  return (
    <div className={styles.wrapper}>
      <LandscapeIndicatorVerificationApproachTemplate
        setLanguage={setLanguage}
        selectedLanguage={selectedLanguage}
        fromLanguage={fromLanguage}
        setFromLanguage={setFromLanguage}
        toLanguage={toLanguage}
        setToLanguage={setToLanguage}
        generateTranslation={generateTranslation}
        generateAllTranslations={generateAllTranslations}
        content={content}
        setContent={setContent}
        callbackFn={callbackFn}
        published={published}
        setPublished={setPublished}
      />
    </div>
  );
};

export default LandscapeIndicatorVerificationApproachPage;
